import React, { useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Heading } from "../common/Heading/Heading";
import { Button } from "../common/Button/Button";
import { COLORS } from "../../assets/constants/styles";
import { Table } from "../common/Table/Table";
import { ActionButton } from "../common/Table/ActionButton/ActionButton";
import { EyeIcon } from "../../assets/images/EyeIcon";
import { LoadingIndicator } from "../common/LoadingIndicator/LoadingIndicator";
import { useLogs } from "../../context/LogsContext";
import { Pagination } from "./Pagination/Pagination";
import { useBlockTestsPagination } from "../../hooks/useBlockTestsPagination";
import { useBlockTestsSearch } from "../../hooks/useBlockTestsSearch";
import {
  Container,
  TitleSection,
  HeadingWrapper,
  Subtitle,
  ControlsRow,
  ErrorMessage,
  RetryLink,
} from "./BlockTest.styles";

export const BlockTest = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scrollRef = useRef(null);

  const initialLoadAttempted = useRef(false);
  const isMounted = useRef(true);

  const {
    blockTests,
    isLoadingBlockTests,
    error,
    fetchBlockTestsForPage,
    resetBlockTests,
  } = useLogs();

  const {
    searchMode,
    isSearching,
    currentSearchPageRef,
    effectivePagination,
    getCurrentSearchQuery,
    hasApiError,
  } = useBlockTestsSearch();

  const { pageButtons, showPagination } = useBlockTestsPagination({
    effectivePagination,
    isSearching,
    currentSearchPageRef,
    getCurrentSearchQuery,
    searchMode,
  });

  useEffect(() => {
    isMounted.current = true;

    if (!initialLoadAttempted.current) {
      initialLoadAttempted.current = true;

      const fetchData = async () => {
        fetchBlockTestsForPage(1);
      };

      fetchData();
    }

    return () => {
      isMounted.current = false;
      resetBlockTests();
    };
  }, [resetBlockTests]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCreateNewTest = useCallback(() => {
    navigate("/block-test/new");
    window.scrollTo(0, 0);
  }, [navigate]);

  const handleViewTest = useCallback(
    (test) => {
      navigate(`/block-test/${test.id}`);
      window.scrollTo(0, 0);
    },
    [navigate]
  );

  const handleRetry = useCallback(() => {
    resetBlockTests();

    setTimeout(() => {
      fetchBlockTestsForPage(1);
    }, 100);
  }, [resetBlockTests, fetchBlockTestsForPage]);

  const handlePageChange = useCallback(
    (newPage) => {
      if (isLoadingBlockTests) return;

      fetchBlockTestsForPage(newPage);
      window.scrollTo(0, 0);
    },
    [fetchBlockTestsForPage, isLoadingBlockTests]
  );

  const columns = [
    { key: "name", header: t("test.blockTestName"), maxWidth: "200px" },
    { key: "organization", header: t("test.organization"), maxWidth: "200px" },
    { key: "chatCount", header: t("test.chatCount") },
    { key: "createdAt", header: t("common.createdAt") },
    { key: "actions", header: t("common.actions") },
  ];

  const renderCustomCell = useCallback(
    (item, key) => {
      switch (key) {
        case "createdAt":
          return new Date(item.createdAt).toLocaleDateString();
        case "actions":
          return (
            <ActionButton
              onClick={() => handleViewTest(item)}
              ariaLabel={t("common.view")}
              color={COLORS.violet}
              hoverColor={COLORS.darkViolet}
            >
              <EyeIcon />
            </ActionButton>
          );
        default:
          return item[key];
      }
    },
    [handleViewTest, t]
  );

  if (isLoadingBlockTests && blockTests.length === 0 && !hasApiError) {
    return (
      <LoadingIndicator
        key="block-test-loading"
        message={t("common.loading")}
      />
    );
  }

  const componentKey = `block-test-${hasApiError ? "error" : "normal"}-${
    isSearching ? "search" : "standard"
  }`;

  return (
    <Container ref={scrollRef} key={componentKey}>
      <TitleSection>
        <HeadingWrapper>
          <Heading text={t("test.blockTestPage")} size={1} />
          <Subtitle>{t("test.blockTestPageDescription")}</Subtitle>
        </HeadingWrapper>
        <ControlsRow>
          <Button
            text={t("test.createNewBlockTest")}
            onClick={handleCreateNewTest}
            variant="violet"
          />
          {hasApiError && (
            <Button
              text={t("common.retry")}
              onClick={handleRetry}
              variant="white"
            />
          )}
        </ControlsRow>
      </TitleSection>

      {error && (
        <ErrorMessage>
          {error}{" "}
          <RetryLink onClick={handleRetry}>{t("common.retry")}</RetryLink>
        </ErrorMessage>
      )}

      <Table
        columns={columns}
        data={blockTests}
        emptyMessage={
          hasApiError
            ? t("errors.connectionError")
            : t("test.noBlockTestsAvailable")
        }
        loadingMessage={t("common.loading")}
        isLoading={isLoadingBlockTests && blockTests.length > 0 && !hasApiError}
        renderCustomCell={renderCustomCell}
        onRowClick={handleViewTest}
      />

      {showPagination && !hasApiError && (
        <Pagination
          currentPage={effectivePagination.page}
          total={effectivePagination.total}
          limit={effectivePagination.limit}
          pageButtons={pageButtons}
          onPageChange={handlePageChange}
        />
      )}
    </Container>
  );
};
